import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useToast } from "../../../../hooks/toast";
import { obterPerfil, salvarPerfil } from "../../../../services/gobidPerfilApi";

// Estado do formulário do Perfil do embarcador (H2 PO1-3529, épico PO1-3527).
// Molde do Parametros/porAssunto/useFormularioParametros: carrega o perfil do
// embarcador selecionado, guarda num mapa só o que o usuário alterou (voltar ao
// valor gravado tira o campo do mapa), salva e recarrega. O PUT leva o perfil
// inteiro como está na tela ("o que você vê é o que fica salvo"), porque o
// backend grava a linha completa; o mapa de alterados serve à barra de salvar.

// Valores de um embarcador sem perfil gravado: os mesmos padrões da tabela.
export const PERFIL_VAZIO = {
  apresentacao: "",
  segmento: "",
  tipoCarga: "",
  prazoPagamento: "",
  agendamento: "",
  descarga: "",
  seguro: "",
  gerenciamentoRisco: "",
  cidadesOrigem: null, // null = cidades das filiais
  contatoNome: "",
  contatoEmail: "",
  contatoTelefone: "",
  contatoWhatsapp: "",
  mostrarContatoNome: true,
  mostrarContatoEmail: true,
  mostrarContatoTelefone: false,
  mostrarContatoWhatsapp: false,
  mostrarApresentacao: true,
  mostrarSegmento: true,
  mostrarTipoCarga: true,
  mostrarPrazoPagamento: true,
  mostrarExigencias: true,
  mostrarCidadesOrigem: true,
  mostrarIndicadores: false,
};

const CAMPOS = Object.keys(PERFIL_VAZIO);

// Normaliza o que veio do backend para os tipos do formulário (texto vazio em
// vez de null; booleanos; ids de cidade como números).
export function perfilParaFormulario(perfil) {
  const base = { ...PERFIL_VAZIO };
  if (!perfil) return base;
  CAMPOS.forEach((campo) => {
    const v = perfil[campo];
    if (campo === "cidadesOrigem") {
      base[campo] = Array.isArray(v) ? v.map(Number).filter((n) => n > 0) : null;
    } else if (typeof PERFIL_VAZIO[campo] === "boolean") {
      base[campo] = v == null ? PERFIL_VAZIO[campo] : Boolean(v);
    } else {
      base[campo] = v == null ? "" : String(v);
    }
  });
  return base;
}

function iguais(a, b) {
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
    return a.every((x, i) => x === b[i]);
  }
  return a === b;
}

export default function useFormularioPerfil(embarcadorId) {
  const { addToast } = useToast();
  const [dados, setDados] = useState(null); // resposta crua do GET
  const [gravado, setGravado] = useState(PERFIL_VAZIO);
  const [alterados, setAlterados] = useState({});
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState("");
  const [salvando, setSalvando] = useState(false);
  const montadoRef = useRef(true);

  useEffect(() => {
    montadoRef.current = true;
    return () => {
      montadoRef.current = false;
    };
  }, []);

  const notify = useCallback(
    (type, title, description) =>
      addToast({
        place: "tr",
        type,
        title,
        description,
        icon: type === "success" ? "tim-icons icon-check-2" : type === "danger" ? "tim-icons icon-alert-circle-exc" : "tim-icons icon-bell-55",
      }),
    [addToast],
  );

  const aplicar = useCallback((r) => {
    setDados(r);
    setGravado(perfilParaFormulario(r && r.perfil));
    setAlterados({});
  }, []);

  const recarregar = useCallback(async () => {
    setAlterados({});
    if (!embarcadorId) {
      setDados(null);
      setGravado(PERFIL_VAZIO);
      setErro("");
      return;
    }
    setCarregando(true);
    setErro("");
    try {
      const r = await obterPerfil(embarcadorId);
      if (!montadoRef.current) return;
      aplicar(r);
    } catch (e) {
      if (!montadoRef.current) return;
      const mensagem = (e.response && e.response.data && e.response.data.error) || "Não foi possível carregar o perfil.";
      setErro(mensagem);
      setDados(null);
      setGravado(PERFIL_VAZIO);
      notify("danger", "Erro ao carregar", mensagem);
    } finally {
      if (montadoRef.current) setCarregando(false);
    }
  }, [embarcadorId, notify, aplicar]);

  useEffect(() => {
    recarregar();
  }, [recarregar]);

  const setValor = useCallback(
    (campo, valor) => {
      setAlterados((prev) => {
        const next = { ...prev };
        if (iguais(gravado[campo], valor)) delete next[campo];
        else next[campo] = valor;
        return next;
      });
    },
    [gravado],
  );

  const descartar = useCallback(() => setAlterados({}), []);

  const valores = useMemo(() => ({ ...gravado, ...alterados }), [gravado, alterados]);
  const totalAlterados = Object.keys(alterados).length;

  const salvar = useCallback(async () => {
    if (!totalAlterados) {
      notify("info", "Nada a salvar", "Nenhum campo do perfil foi alterado.");
      return null;
    }
    setSalvando(true);
    try {
      const r = await salvarPerfil(embarcadorId, valores);
      if (!montadoRef.current) return r;
      notify("success", "Perfil salvo", "O próximo convite para transportadoras de fora da carteira já usa este perfil.");
      aplicar(r);
      return r;
    } catch (e) {
      if (!montadoRef.current) return null;
      const data = (e.response && e.response.data) || {};
      const detalhe = data.errors ? Object.keys(data.errors).map((k) => data.errors[k]).join(" ") : "";
      notify("danger", "Erro ao salvar", `${data.error || "Não foi possível salvar o perfil."}${detalhe ? ` ${detalhe}` : ""}`);
      return null;
    } finally {
      if (montadoRef.current) setSalvando(false);
    }
  }, [embarcadorId, valores, totalAlterados, notify, aplicar]);

  return {
    carregando,
    erro,
    dados,
    valores,
    alterados,
    totalAlterados,
    setValor,
    descartar,
    salvar,
    salvando,
    recarregar,
  };
}
